import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "~/utils/trpc";
import { CandidateDetailsPage } from "./-pages/candidateDetails";
import { z } from "zod";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { useEffect, useRef } from "react";
import { sanitizeDocumentTitlePart, useDocumentTitle } from "~/utils/document-title";

const candidateDetailSearchSchema = z.object({
  view: z
    .enum([
      "details",
      "notes",
      "activity",
      "history",
      "documents",
      "communications",
      "memory",
      "placements",
      "submissions",
      "cv-sends",
      "chat-projects",
    ])
    .optional()
    .default("details"),
});

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/candidates/$candidateId")({
  staleTime: 0,
  validateSearch: candidateDetailSearchSchema,
  gcTime: 0,
  component: CandidateDetailsComponent,
});

function CandidateDetailsComponent() {
  const search = Route.useSearch();
  const { datasetSlug, candidateId } = useParams({ from: "/_dashboard/$datasetSlug/data/candidates/$candidateId" });
  const {
    data: candidate,
    refetch,
    status,
  } = useQuery(trpc.candidates.getById.queryOptions({ id: candidateId }, { staleTime: 0, refetchOnMount: true }));
  const hasMountedRef = useRef(false);
  const candidateName = sanitizeDocumentTitlePart(
    [candidate?.first_name, candidate?.last_name].filter(Boolean).join(" ") || candidate?.email,
    "Candidate",
  );

  useDocumentTitle(candidateName, "Candidate");

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    void refetch();
  }, [search, refetch]);

  useBreadCrumbs([
    { title: "Candidates", link: `/${datasetSlug}/data/candidates` },
    { title: candidateName, link: `/${datasetSlug}/data/candidates/${candidate?.id}` },
  ]);
  if (status === "pending" && !candidate) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  return <CandidateDetailsPage candidate={candidate} onRefresh={refetch} />;
}
